package com.speedsoftware.nibbler.ui.profile

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.ExperimentalLayoutApi
import androidx.compose.foundation.layout.FlowRow
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.Button
import androidx.compose.material3.ButtonDefaults
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.OutlinedButton
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.speedsoftware.nibbler.models.UpdateProfileRequest
import com.speedsoftware.nibbler.ui.AppState
import com.speedsoftware.nibbler.ui.Screen
import com.speedsoftware.nibbler.ui.components.ResponsiveScaffold
import com.speedsoftware.nibbler.ui.components.UserAvatar
import com.speedsoftware.nibbler.ui.components.EmojiGlyph
import com.speedsoftware.nibbler.ui.theme.NibblerColors
import kotlinx.coroutines.launch

/** Emojis offered as profile icons. */
val ProfileEmojiChoices = listOf(
    "😀", "😎", "🤓", "🥳", "😺", "🐶", "🦊", "🐼", "🦁", "🐸", "🐙", "🦄",
    "🐝", "🦉", "🚀", "⭐", "🔥", "⚡", "🌈", "🍀", "🌻", "🍕", "🍩", "☕",
    "🎮", "🎧", "🎨", "🎸", "💡", "🧠", "🤖", "👾", "💎", "🏆"
)

@OptIn(ExperimentalLayoutApi::class)
@Composable
fun ProfileSection(appState: AppState) {
    val user = appState.currentUser
    val scope = rememberCoroutineScope()

    var name by remember { mutableStateOf(user?.name ?: "") }
    var avatarUrl by remember { mutableStateOf(user?.avatarUrl ?: "") }
    var avatarEmoji by remember { mutableStateOf(user?.avatarEmoji ?: "") }
    var status by remember { mutableStateOf<String?>(null) }
    var isSaving by remember { mutableStateOf(false) }

            Column(
                modifier = Modifier
                    .widthIn(max = 560.dp)
                    .fillMaxWidth()
                    .background(NibblerColors.SurfaceContainer, RoundedCornerShape(12.dp))
                    .border(1.dp, NibblerColors.OutlineVariant, RoundedCornerShape(12.dp))
                    .padding(20.dp),
                verticalArrangement = Arrangement.spacedBy(14.dp)
            ) {
                // Live preview of the avatar as other people will see it
                Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(14.dp)) {
                    UserAvatar(
                        user = user?.copy(
                            name = name.ifBlank { user.name },
                            avatarUrl = avatarUrl.trim().ifEmpty { null },
                            avatarEmoji = avatarEmoji.ifEmpty { null }
                        ),
                        size = 64.dp
                    )
                    Column {
                        Text(name.ifBlank { user?.name ?: "" }, fontSize = 18.sp, color = NibblerColors.OnSurface)
                        Text(user?.email ?: "", fontSize = 12.sp, color = NibblerColors.OnSurfaceVariant)
                    }
                }

                OutlinedTextField(
                    value = name,
                    onValueChange = { name = it },
                    label = { Text("Display name") },
                    singleLine = true,
                    modifier = Modifier.fillMaxWidth()
                )

                OutlinedTextField(
                    value = avatarUrl,
                    onValueChange = { avatarUrl = it },
                    label = { Text("Icon image URL (https://...)") },
                    singleLine = true,
                    modifier = Modifier.fillMaxWidth()
                )
                Text(
                    "The image server must allow cross-origin requests, otherwise the initials or emoji are shown instead.",
                    fontSize = 11.sp,
                    color = NibblerColors.OnSurfaceVariant
                )

                Text("Or pick an emoji:", style = MaterialTheme.typography.labelMedium, color = NibblerColors.OnSurface)
                FlowRow(horizontalArrangement = Arrangement.spacedBy(6.dp), verticalArrangement = Arrangement.spacedBy(6.dp)) {
                    ProfileEmojiChoices.forEach { emoji ->
                        val isSelected = emoji == avatarEmoji
                        Box(
                            modifier = Modifier
                                .size(40.dp)
                                .background(
                                    if (isSelected) NibblerColors.SecondaryContainer else NibblerColors.SurfaceContainerHigh,
                                    RoundedCornerShape(8.dp)
                                )
                                .border(
                                    if (isSelected) 2.dp else 1.dp,
                                    if (isSelected) NibblerColors.BauhausYellow else NibblerColors.OutlineVariant,
                                    RoundedCornerShape(8.dp)
                                )
                                .clickable { avatarEmoji = if (isSelected) "" else emoji },
                            contentAlignment = Alignment.Center
                        ) {
                            EmojiGlyph(emoji, size = 24.dp)
                        }
                    }
                }
                Text(
                    "An uploaded image URL takes precedence over the emoji. Without either, your initials are used.",
                    fontSize = 11.sp,
                    color = NibblerColors.OnSurfaceVariant
                )

                Spacer(modifier = Modifier.height(4.dp))

                Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(12.dp)) {
                    Button(
                        enabled = !isSaving && name.isNotBlank(),
                        onClick = {
                            isSaving = true
                            status = null
                            scope.launch {
                                appState.apiClient.updateProfile(
                                    UpdateProfileRequest(
                                        name = name.trim(),
                                        avatarUrl = avatarUrl.trim(),
                                        avatarEmoji = avatarEmoji
                                    )
                                ).onSuccess {
                                    appState.currentUser = it
                                    status = "Profile saved."
                                }.onFailure {
                                    status = "Could not save the profile. Check that the image URL starts with http:// or https://."
                                }
                                isSaving = false
                            }
                        },
                        shape = RoundedCornerShape(8.dp),
                        colors = ButtonDefaults.buttonColors(containerColor = NibblerColors.Primary)
                    ) { Text("Save profile") }

                    status?.let { Text(it, fontSize = 12.sp, color = NibblerColors.OnSurfaceVariant) }
                }
            }

}
